import React, { useEffect, useMemo, useState } from "react";
import Offcanvas from "react-bootstrap/Offcanvas";
import Modal from "react-bootstrap/Modal";
import Accordion from "react-bootstrap/Accordion";
import classNames from "classnames";
import { formOffCanvas } from "@/assets/styles/common.module.scss";
import Image from "next/image";
import { IMAGE_BASE_PATH } from "@/config/config";
import PropTypes from "prop-types";
import { formSubmitButton } from "@/assets/styles/common.module.scss";
import { isMobile, toAmount, toAmountFloat } from "@/helpers/utils";
import styles from "./packageModal.module.scss";
import CustomToggle from "./CustomToggle";
import { showInfoToast } from "@/helpers/toastr";
import { PACKAGES_COVERS_ENABLED_SUBPRODUCT_CODES, HEALTH_SUPERCHARGE_CODE } from "@/config/constants";

const COVER_CODES = {
  AGGREGATE_DEDUCTIBLE: "C008",
  MATERNITY: "C015",
  MATERNITY_REDUCTION: "C016",
  INFINITE_ADVANTAGE: "C017",
  EARLY_ACCESS: "C018",
};

const ADVANCE_COVER_KEYS = new Set(["ADVANCE_COVER", "ADVANCED_COVER"]);

const PackageCanvasMenu = React.memo(
  ({
    packages,
    userCanOptOutMandatory,
    readOnly,
    onUpdate,
    addonCovers,
    optedCovers,
    packageCount,
    subProductCode,
    toProductCode,
  }) => {
    console.log("OPC", optedCovers, packages);
    const isCoversUIEnabled = useMemo(() => {
      return PACKAGES_COVERS_ENABLED_SUBPRODUCT_CODES.has(
        String(subProductCode || "")
      );
    }, [subProductCode]);

    const isPackageUIEnabled = useMemo(() => {
      return (
        String(subProductCode || "") !== HEALTH_SUPERCHARGE_CODE
      );
    }, [subProductCode, toProductCode]);

    const [show, setShow] = useState(false);
    const [packagesObj, setPackagesObj] = useState({});
    const [optedCoversObj, setOptedCoversObject] = useState({});
    const [packageDropdownState, setPackageDropdownState] = useState({});
    const [selectedVAD, setSelectedVAD] = useState(null);
    const [vadEnabled, setVadEnabled] = useState(false);
    const [vadKeys, setVadKeys] = useState([]);
    const [vadKeyToAmount, setVadKeyToAmount] = useState({});
    const [activeKeys, setActiveKeys] = useState(() =>
      readOnly ? ["0"] : ["1"]
    );
    const [coverActiveKeys, setCoverActiveKeys] = useState(["0", "1"]);
    const [hasMandatoryVAD, setHasMandatoryVAD] = useState(false);
    const [initialVADKey, setInitialVADKey] = useState(null);
    const [initialVADAmount, setInitialVADAmount] = useState(0);
    const [activeView, setActiveView] = useState(isPackageUIEnabled ? "packages" : "covers");

    const VAD_LABELS = {
      VOLUNTARY_AGGREGATE_DEDUCTIBLE_RS_25000: "25,000",
      VOLUNTARY_AGGREGATE_DEDUCTIBLE: "50,000",
      VOLUNTARY_AGGREGATE_DEDUCTIBLE_RS_100000: "1,00,000",
    };

    const isCoverInitiallyIncluded = (cover) => {
      if (!cover) return false;
      if (typeof cover.isIncludedCover === "boolean") {
        return cover.isIncludedCover;
      }
      if (cover.opted === true) return true;
      if (Array.isArray(cover.value)) {
        return cover.value.some((item) => item?.opted === true);
      }
      return false;
    };

    const resetPackagesState = () => {
      const clonedPackages = JSON.parse(JSON.stringify(packages || {}));

      Object.keys(clonedPackages).forEach((key) => {
        const riders = (clonedPackages[key].riders || []).map((riderCode) => {
          if (addonCovers?.[riderCode]) {
            return {
              name: addonCovers[riderCode].name,
              description: addonCovers[riderCode].description,
            };
          }
          return {
            name: "Unknown Rider",
            description: "No description available",
          };
        });
        clonedPackages[key].riderDescriptions = riders;
      });

      const vadCandidates = Object.entries(clonedPackages).filter(([key]) =>
        key.startsWith("VOLUNTARY_AGGREGATE_DEDUCTIBLE")
      );

      const vadAmountFallback = {
        VOLUNTARY_AGGREGATE_DEDUCTIBLE: 50000,
        VOLUNTARY_AGGREGATE_DEDUCTIBLE_RS_25000: 25000,
        VOLUNTARY_AGGREGATE_DEDUCTIBLE_RS_100000: 100000,
      };

      const hasOriginalVadSelection = vadCandidates.some(
        ([, value]) =>
          value?.preSelected === true || value?.optedInPreviousPolicy === true
      );
      if (!hasOriginalVadSelection) {
        vadCandidates.forEach(([key]) => {
          if (clonedPackages[key]) {
            clonedPackages[key].preSelected =
              clonedPackages[key]?.isOpted === true;
          }
        });
      }

      const sortedVadEntries = vadCandidates
        .map(([key, value]) => {
          const match = value?.name?.match(/(\d{2,6})/);
          const amount = match
            ? parseInt(match[1].replace(/,/g, ""), 10)
            : vadAmountFallback[key] || 0;
          return { key, amount };
        })
        .sort((a, b) => a.amount - b.amount)
        .map((e) => e.key);

      setVadKeys(sortedVadEntries);

      const keyToAmt = Object.fromEntries(
        sortedVadEntries.map((k) => {
          const label = clonedPackages[k]?.name || "";
          const m = label.match(/(\d{2,6})/);
          const amt = m
            ? parseInt(m[1].replace(/,/g, ""), 10)
            : vadAmountFallback[k] || 0;
          return [k, amt];
        })
      );
      setVadKeyToAmount(keyToAmt);

      const mandatory = sortedVadEntries.some(
        (k) => clonedPackages[k]?.isMandatory
      );
      setHasMandatoryVAD(mandatory);

      const vadSelected = sortedVadEntries.find(
        (key) => clonedPackages[key]?.isOpted
      );
      const vadOriginal = sortedVadEntries.find(
        (key) =>
          clonedPackages[key]?.preSelected === true ||
          clonedPackages[key]?.optedInPreviousPolicy === true
      );
      setSelectedVAD(vadSelected || sortedVadEntries[0] || null);
      setVadEnabled(mandatory ? true : !!vadSelected);

      if (vadOriginal || vadSelected) {
        const initialKey = vadOriginal || vadSelected;
        setInitialVADKey(initialKey);
        setInitialVADAmount(keyToAmt[initialKey] || 0);
      } else {
        setInitialVADKey(null);
        setInitialVADAmount(0);
      }

      setPackagesObj(clonedPackages);

      const clonedCovers = JSON.parse(JSON.stringify(optedCovers || {}));
      Object.keys(clonedCovers).forEach((key) => {
        const cover = clonedCovers[key];
        if (!cover) return;
        cover.isIncludedCover = isCoverInitiallyIncluded(cover);
        if (typeof cover.opted === "undefined" && Array.isArray(cover.value)) {
          cover.opted = cover.value.some((item) => item?.opted === true);
        }
        if (
          isAggregateDeductibleCover(key) &&
          Array.isArray(cover.value) &&
          !cover.value.some(
            (item) =>
              item?.preSelected === true || item?.optedInPreviousPolicy === true
          )
        ) {
          cover.value = cover.value.map((item) => ({
            ...item,
            preSelected: item?.opted === true,
          }));
        }
      });
      setOptedCoversObject(clonedCovers);
    };

    useEffect(() => {
      resetPackagesState();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [packages, addonCovers, optedCovers]);

    useEffect(() => {
      if (!isCoversUIEnabled && activeView !== "packages") {
        setActiveView("packages");
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isCoversUIEnabled]);

    const toggleDropdown = (name) => {
      setPackageDropdownState((prev) => ({ ...prev, [name]: !prev[name] }));
    };

    const isAdvanceCoverOpted = (pkgs) =>
      Object.keys(pkgs || {}).some(
        (k) => ADVANCE_COVER_KEYS.has(k) && pkgs?.[k]?.isOpted
      );

    const deselectAdvanceCover = (pkgs) => {
      const updated = { ...(pkgs || {}) };
      Object.keys(updated).forEach((k) => {
        if (ADVANCE_COVER_KEYS.has(k) && updated[k]?.isOpted) {
          updated[k] = { ...updated[k], isOpted: false };
        }
      });
      return updated;
    };

    const deselectCover = (coversMap, coverCode) => {
      const updated = { ...(coversMap || {}) };
      const c = updated?.[coverCode];
      if (!c) return updated;

      const next = { ...c, opted: false };
      if (Array.isArray(next.value)) {
        next.value = next.value.map((o) => ({ ...o, opted: false }));
      }
      updated[coverCode] = next;
      return updated;
    };

    const ensureCoverSelectedSingle = (coversMap, coverCode) => {
      const updated = { ...(coversMap || {}) };
      const c = updated?.[coverCode];
      if (!c) return updated;

      const next = { ...c, opted: true };
      if (Array.isArray(next.value)) {
        const options = next.value.map((o) => ({ ...o }));
        let idx = options.findIndex((o) => o?.canBeOpted !== false);
        if (idx < 0) idx = 0;
        options.forEach((o, i) => (o.opted = i === idx));
        next.value = options;
      }
      updated[coverCode] = next;
      return updated;
    };

    const getCanBeDeOpted = (cover) => {
      return cover?.canBeDeOpted === true || cover?.canBeOpted === true;
    };

    const isAggregateDeductibleCover = (coverCode) =>
      coverCode === COVER_CODES.AGGREGATE_DEDUCTIBLE;

    const getCoverOptionAmount = (option) => {
      const amount = Number(String(option?.name || "").replace(/,/g, ""));
      return Number.isFinite(amount) ? amount : 0;
    };

    const getSelectedCoverOptionAmount = (cover) => {
      if (!Array.isArray(cover?.value)) return 0;
      const selectedOption = cover.value.find(
        (option) => option?.opted === true
      );
      return getCoverOptionAmount(selectedOption);
    };

    const hasOriginalCoverOption = (cover) => {
      if (!Array.isArray(cover?.value)) return false;
      return cover.value.some(
        (option) =>
          option?.preSelected === true || option?.optedInPreviousPolicy === true
      );
    };

    const getCoverOptionFloorAmount = (cover) => {
      if (!Array.isArray(cover?.value)) return 0;
      const originalOption = cover.value.find(
        (option) =>
          option?.preSelected === true || option?.optedInPreviousPolicy === true
      );
      return (
        getCoverOptionAmount(originalOption) ||
        getSelectedCoverOptionAmount(cover)
      );
    };

    const canSelectCoverOption = (coverCode, cover, option) => {
      if (readOnly || !cover?.opted) return false;

      if (isAggregateDeductibleCover(coverCode)) {
        if (!hasOriginalCoverOption(cover)) return true;

        const selectedAmount = getCoverOptionFloorAmount(cover);
        const optionAmount = getCoverOptionAmount(option);
        return optionAmount >= selectedAmount;
      }

      return true;
    };

    const canUserToggleCover = (cover) => {
      const canBeDeOpted = getCanBeDeOpted(cover);
      if (!readOnly) return true;
      if (canBeDeOpted && cover?.opted) return true;
      return false;
    };

    const togglePackageOptIn = (key, isMandatory) => {
      setPackagesObj((prev) => {
        const current = prev?.[key];
        if (!current) return prev;

        if (isMandatory && !userCanOptOutMandatory) return prev;

        const updated = { ...prev };
        const isOptingIn = !current?.isOpted;
        
        if (ADVANCE_COVER_KEYS.has(key) && current?.isOpted && !isOptingIn && current?.optedInPreviousPolicy) {
          return prev;
        }

        if (isOptingIn && ADVANCE_COVER_KEYS.has(key)) {
          const hasC017 =
            optedCoversObj?.[COVER_CODES.INFINITE_ADVANTAGE]?.opted;
          const hasC018 = optedCoversObj?.[COVER_CODES.EARLY_ACCESS]?.opted;

          if (hasC017 || hasC018) {
            setOptedCoversObject((covPrev) => {
              let covUpdated = { ...(covPrev || {}) };
              if (hasC017)
                covUpdated = deselectCover(
                  covUpdated,
                  COVER_CODES.INFINITE_ADVANTAGE
                );
              if (hasC018)
                covUpdated = deselectCover(
                  covUpdated,
                  COVER_CODES.EARLY_ACCESS
                );

              showInfoToast(
                `${current?.name || "Advance cover"} selected. ${[
                  hasC017
                    ? covPrev?.[COVER_CODES.INFINITE_ADVANTAGE]?.name ||
                      "Infinite advantage"
                    : null,
                  hasC018
                    ? covPrev?.[COVER_CODES.EARLY_ACCESS]?.name ||
                      "Early access"
                    : null,
                ]
                  .filter(Boolean)
                  .join(
                    " and "
                  )} has been deselected as both cannot be opted together.`
              );

              return covUpdated;
            });
          }
        }

        const EXCLUSIVE_CONFLICT_PAIRS = [
          ["SUPERCHARGE_BONUS_RIDER", "CUMULATIVE_BONUS_SHIELD"],
          ["CUMULATIVE_BONUS_SHIELD", "SUPERCHARGE_BONUS_RIDER"],
        ];

        if (userCanOptOutMandatory && isOptingIn) {
          EXCLUSIVE_CONFLICT_PAIRS.forEach(([selected, toDeselect]) => {
            if (key === selected && updated[toDeselect]?.isOpted) {
              updated[toDeselect].isOpted = false;
              showInfoToast(
                `${updated[selected].name} selected. ${updated[toDeselect].name} has been deselected as both cannot be opted together.`
              );
            }
          });
        }

        updated[key] = { ...current, isOpted: isOptingIn };
        return updated;
      });
    };

    const handleVADCheckbox = () => {
      if (hasMandatoryVAD) return;
      const isChecked = !vadEnabled;
      setVadEnabled(isChecked);

      const updated = { ...packagesObj };
      vadKeys.forEach((key) => {
        if (updated[key]) updated[key].isOpted = false;
      });
      if (isChecked && selectedVAD && updated[selectedVAD]) {
        updated[selectedVAD].isOpted = true;
      }
      setPackagesObj(updated);
    };

    const handleVADRadioChange = (key) => {
      setSelectedVAD(key);
      const updated = { ...packagesObj };

      if (!vadEnabled) setVadEnabled(true);

      vadKeys.forEach((k) => {
        if (updated[k]) updated[k].isOpted = false;
      });
      if (updated[key]) updated[key].isOpted = true;

      setPackagesObj(updated);
    };

    const handleExclusiveCheckbox = (selectedKey) => {
      if (!packagesObj?.[selectedKey]) return;
      const updated = { ...packagesObj };
      [
        "CUMULATIVE_BONUS_SHIELD",
        "SUPERCHARGE_RIDER",
        "SUPERCHARGE_BONUS_RIDER",
      ].forEach((key) => {
        if (!updated[key]) return;
        if (key !== selectedKey) updated[key].isOpted = false;
      });
      updated[selectedKey].isOpted = !updated[selectedKey].isOpted;
      setPackagesObj(updated);
    };

    const applyCoverRulesBeforeOptIn = (coversMap, coverCode) => {
      let updated = { ...(coversMap || {}) };

      if (coverCode === COVER_CODES.MATERNITY_REDUCTION) {
        const c015 = updated?.[COVER_CODES.MATERNITY];
        if (c015 && !c015?.opted) {
          updated = ensureCoverSelectedSingle(updated, COVER_CODES.MATERNITY);
          showInfoToast(
            `${
              c015?.name || "Maternity care"
            } has been selected as it is required for ${
              updated?.[COVER_CODES.MATERNITY_REDUCTION]?.name ||
              "Reduction of Maternity Care benefit"
            }.`
          );
        } else if (!c015) {
          showInfoToast(
            `Cannot select ${updated?.[coverCode]?.name || coverCode} without ${
              COVER_CODES.MATERNITY
            }.`
          );
          return { updated: coversMap, blocked: true };
        }
      }

      if (
        coverCode === COVER_CODES.INFINITE_ADVANTAGE ||
        coverCode === COVER_CODES.EARLY_ACCESS
      ) {
        if (isAdvanceCoverOpted(packagesObj)) {
          setPackagesObj((pkPrev) => {
            const pkUpdated = deselectAdvanceCover(pkPrev);
            showInfoToast(
              `${
                updated?.[coverCode]?.name || "Cover"
              } selected. Advance cover has been deselected as both cannot be opted together.`
            );
            return pkUpdated;
          });
        }
      }

      return { updated, blocked: false };
    };

    const handleCoverOptionChangeRadio = (coverCode, optionName) => {
      if (readOnly) return;

      setOptedCoversObject((prev) => {
        const current = prev?.[coverCode];
        if (!current) return prev;
        const selectedOption = Array.isArray(current.value)
          ? current.value.find((option) => option?.name === optionName)
          : null;

        if (
          isAggregateDeductibleCover(coverCode) &&
          !canSelectCoverOption(coverCode, current, selectedOption)
        ) {
          return prev;
        }

        const pre = applyCoverRulesBeforeOptIn(prev, coverCode);
        if (pre.blocked) return prev;

        const next = { ...(pre.updated || {}) };
        const c = next?.[coverCode];
        if (!c) return prev;

        const updatedCover = { ...c, opted: true };
        if (Array.isArray(updatedCover.value)) {
          updatedCover.value = updatedCover.value.map((o) => ({
            ...o,
            opted: o?.name === optionName,
          }));
        }
        next[coverCode] = updatedCover;
        return next;
      });
    };

    const handleOptedCoversChange = (selectedKey) => {
      setOptedCoversObject((prev) => {
        const current = prev?.[selectedKey];
        if (!current) return prev;

        const canToggle = canUserToggleCover(current);
        if (!canToggle) return prev;
        if (
          isAggregateDeductibleCover(selectedKey) &&
          current?.opted &&
          current?.isIncludedCover
        ) {
          return prev;
        }

        const nextOpted = !current.opted;

        if (
          readOnly &&
          getCanBeDeOpted(current) &&
          current?.opted &&
          nextOpted === true
        ) {
          return prev;
        }

        let updated = { ...prev };

        if (selectedKey === COVER_CODES.MATERNITY && !nextOpted) {
          const c016 = updated?.[COVER_CODES.MATERNITY_REDUCTION];
          if (c016?.opted) {
            updated = deselectCover(updated, COVER_CODES.MATERNITY_REDUCTION);
            showInfoToast(
              `${
                c016?.name || "Reduction of Maternity Care benefit"
              } has been deselected as it requires ${
                current?.name || "Maternity care"
              }.`
            );
          }
        }

        if (nextOpted) {
          const pre = applyCoverRulesBeforeOptIn(updated, selectedKey);
          if (pre.blocked) return prev;
          updated = pre.updated;
        }

        const updatedCover = { ...updated[selectedKey], opted: nextOpted };

        if (Array.isArray(updatedCover.value)) {
          const options = updatedCover.value.map((o) => ({ ...o }));
          if (nextOpted) {
            let idx = options.findIndex((o) => o?.canBeOpted !== false);
            if (idx < 0) idx = 0;
            options.forEach((o, i) => (o.opted = i === idx));
          } else {
            options.forEach((o) => (o.opted = false));
          }
          updatedCover.value = options;
        }

        updated[selectedKey] = updatedCover;
        return updated;
      });
    };

    const coverHasAnyNonZeroValue = (cover) => {
      if (!cover) return false;
      if (Array.isArray(cover.value)) {
        return cover.value.some((o) => Number(o?.value || 0) !== 0);
      }
      return Number(cover.value || 0) !== 0;
    };

    const handleAdvanceCover = ( key, cover) => {
      if (!cover) return false;
      const isHealthSupercharge = String(subProductCode || "") === HEALTH_SUPERCHARGE_CODE;
      if(Array.isArray(cover.value) && isHealthSupercharge && key === "C004") {
        return !cover.value.every((o) => o?.opted === false && o?.canBeOpted === false);
      }
      return true;
    }

    const getCoverDisplayAmount = (cover) => {
      if (!cover) return 0;

      if (Array.isArray(cover.value)) {
        const opted = cover.value.filter((o) => o?.opted === true);
        if (opted.length) {
          return opted.reduce(
            (sum, o) => sum + (Number(o?.value || 0) || 0),
            0
          );
        }
        const first = cover.value?.[0];
        return Number(first?.value || 0) || 0;
      }

      return Number(cover.value || 0) || 0;
    };

    const formatCoverAmount = (amount) => {
      const numericAmount = Number(amount || 0);
      const absoluteAmount = Math.abs(numericAmount);
      const formattedAmount = Number.isInteger(absoluteAmount)
        ? toAmount(absoluteAmount.toString())
        : toAmountFloat(absoluteAmount.toString());

      return `${numericAmount < 0 ? "-" : ""}₹ ${formattedAmount}`;
    };

    const formatCoverOptionName = (name) => {
      const numericName = Number(name);
      if (!Number.isFinite(numericName)) return name;
      return toAmount(numericName.toString());
    };

    // ======= PACKAGES UI =======

    let EXCLUSIVE_KEYS = [
      "CUMULATIVE_BONUS_SHIELD",
      "SUPERCHARGE_RIDER",
      "SUPERCHARGE_BONUS_RIDER",
    ];

    const getVisiblePackageGroups = (pkgEntries, isOptional) => {
      let exclusivePackageKeys = [
        "CUMULATIVE_BONUS_SHIELD",
        "SUPERCHARGE_RIDER",
        "SUPERCHARGE_BONUS_RIDER",
      ];

      const exclusiveEntries = pkgEntries.filter(([key]) =>
        exclusivePackageKeys.includes(key)
      );
      const exclusivePresentKeys = exclusiveEntries.map(([key]) => key);
      const hasMandatoryExclusive = exclusiveEntries.some(
        ([, pkg]) => pkg?.isMandatory
      );

      const treatExclusivesAsNormal =
        hasMandatoryExclusive || exclusivePresentKeys.length <= 1;
      if (treatExclusivesAsNormal) exclusivePackageKeys = [];

      const exclusivePackages = treatExclusivesAsNormal
        ? []
        : pkgEntries.filter(([key]) => exclusivePackageKeys.includes(key));

      const nonExclusivePackages = pkgEntries.filter(([key, pkg]) => {
        const isExclusive = !userCanOptOutMandatory
          ? EXCLUSIVE_KEYS.includes(key)
          : exclusivePackageKeys.includes(key);
        const isVAD = vadKeys.includes(key);
        if (isVAD) return false;
        if (isOptional && isExclusive) return false;
        if (key === "WOMEN_SURAKSHA" && pkg?.value === 0) return false;
        return true;
      });

      return { exclusivePackages, nonExclusivePackages };
    };

    const renderVADSection = () =>
      vadKeys.length > 0 && (
        <div className="package-group mb-3">
          <div className="label-checkbox-container d-flex justify-content-between align-items-center">
            <label className="package-label m-0">
              <span>
                <Image
                  src={`${IMAGE_BASE_PATH}/images/StarIcon.svg`}
                  alt="X"
                  width={15}
                  height={15}
                />
              </span>
              Voluntary Aggregate Deductible
            </label>
            {!readOnly && !hasMandatoryVAD && (
              <input
                type="checkbox"
                checked={vadEnabled}
                onChange={handleVADCheckbox}
              />
            )}
          </div>

          <div className={styles.radioGroup}>
            {vadKeys.map((key) => {
              const amount = vadKeyToAmount[key] ?? 0;
              const disableLower = initialVADKey
                ? amount < initialVADAmount
                : false;

              return (
                <div key={key} className={styles.radioInputWrapper}>
                  <input
                    className={styles.radioInput}
                    type="radio"
                    name="vad-option"
                    id={`vad-${key}`}
                    checked={selectedVAD === key && vadEnabled}
                    onChange={() => handleVADRadioChange(key)}
                    disabled={
                      readOnly ||
                      (!hasMandatoryVAD && !vadEnabled) ||
                      disableLower
                    }
                  />
                  <label className="form-check-label" htmlFor={`vad-${key}`}>
                    ₹ {VAD_LABELS[key] || ""}
                  </label>
                </div>
              );
            })}
            <span className={styles.vadDescription}>
              Our liability under the base Policy shall be subject to
              application of Aggregate Deductible as specified.
            </span>
          </div>
        </div>
      );

    const renderPackageGroup = (pkgEntries, isOptional) => {
      const { exclusivePackages, nonExclusivePackages } =
        getVisiblePackageGroups(pkgEntries, isOptional);

      return (
        <>
          {nonExclusivePackages.map(([key, pkg]) => (
            <div key={key} className="package-group mb-3">
              <div className="label-checkbox-container d-flex justify-content-between align-items-center">
                <label
                  htmlFor={`package${pkg?.name}`}
                  className="package-label m-0"
                >
                  <span>
                    <Image
                      src={`${IMAGE_BASE_PATH}/images/StarIcon.svg`}
                      alt="X"
                      width={15}
                      height={15}
                    />
                  </span>
                  {pkg?.name}
                </label>
                {!readOnly && (isOptional || userCanOptOutMandatory) && (
                  <input
                    type="checkbox"
                    checked={pkg?.isOpted}
                    onChange={() => togglePackageOptIn(key, pkg?.isMandatory)}
                  />
                )}
              </div>

              <div className="price-dropdown-container d-flex align-items-center justify-content-between mt-1">
                <span className="price-info">
                  ₹
                  {Number.isInteger(pkg?.value + pkg?.gstval)
                    ? toAmount((pkg?.value + pkg?.gstval).toString())
                    : toAmountFloat((pkg?.value + pkg?.gstval).toString())}
                </span>
                <span
                  className="covers-link"
                  onClick={() => toggleDropdown(pkg?.name)}
                >
                  {pkg?.riderDescriptions?.length || 0} covers{" "}
                  {packageDropdownState[pkg?.name] ? "▲" : "▼"}
                </span>
              </div>

              {packageDropdownState[pkg?.name] && (
                <div className="dropdown-section mt-2">
                  <ul>
                    {pkg?.riderDescriptions?.map((rider, idx) => (
                      <li key={idx}>
                        <strong>{rider.name}:</strong>
                        <p>{rider.description}</p>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}

          {exclusivePackages.length > 0 && (
            <div className="package-group mb-3">
              <div className="d-flex align-items-center mb-2">
                <Image
                  src={`${IMAGE_BASE_PATH}/images/StarIcon.svg`}
                  alt="Star"
                  width={15}
                  height={15}
                  className="me-2"
                />
                <span className="package-label m-0">Select any one:</span>
                <input
                  className={styles.styleAnyOne}
                  type="checkbox"
                  checked={exclusivePackages.some(([, pkg]) => pkg?.isOpted)}
                  disabled={!exclusivePackages.some(([, pkg]) => pkg?.isOpted)}
                  onChange={(e) => {
                    if (!e.target.checked) {
                      const updated = Object.fromEntries(
                        exclusivePackages.map(([key, pkg]) => [
                          key,
                          { ...pkg, isOpted: false },
                        ])
                      );
                      setPackagesObj((prev) => ({ ...prev, ...updated }));
                    }
                  }}
                />
              </div>

              {exclusivePackages.map(([key, pkg]) => (
                <div key={key} className="package-group mb-3">
                  <div className="label-checkbox-container ">
                    <label
                      htmlFor={`exclusive-${key}`}
                      className="package-label m-0 d-flex  align-items-center"
                    >
                      <input
                        type="radio"
                        name="exclusive-option"
                        id={`exclusive-${key}`}
                        checked={pkg?.isOpted}
                        onChange={() => handleExclusiveCheckbox(key)}
                        className={styles.exclusiveRadio}
                        disabled={readOnly}
                      />
                      <label className="ms-2">{pkg?.name}</label>
                    </label>
                  </div>

                  <div className="price-dropdown-container d-flex align-items-center justify-content-between mt-1">
                    <span className="price-info">
                      ₹
                      {Number.isInteger(pkg?.value + pkg?.gstval)
                        ? toAmount((pkg?.value + pkg?.gstval).toString())
                        : toAmountFloat((pkg?.value + pkg?.gstval).toString())}
                      <small> (With Tax)</small>
                    </span>
                    <span
                      className="covers-link"
                      onClick={() => toggleDropdown(pkg?.name)}
                    >
                      {pkg?.riderDescriptions?.length || 0} covers{" "}
                      {packageDropdownState[pkg?.name] ? "▲" : "▼"}
                    </span>
                  </div>

                  {packageDropdownState[pkg?.name] && (
                    <div className="dropdown-section mt-2">
                      <ul>
                        {pkg?.riderDescriptions?.map((rider, idx) => (
                          <li key={idx}>
                            <strong>{rider.name}:</strong>
                            <p>{rider.description}</p>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      );
    };

    const includedEntries = Object.entries(packagesObj).filter(
      ([key, pkg]) => pkg?.isMandatory && !vadKeys.includes(key) && coverHasAnyNonZeroValue(pkg)
    );
    const optionalEntries = Object.entries(packagesObj).filter(
      ([key, pkg]) => !pkg?.isMandatory && !vadKeys.includes(key) && coverHasAnyNonZeroValue(pkg)
    );
    const showIncludedPackages = includedEntries.length > 0 || hasMandatoryVAD;
    const visibleOptionalPackageGroups = getVisiblePackageGroups(
      optionalEntries,
      true
    );
    const showOptionalPackages =
      !readOnly &&
      (visibleOptionalPackageGroups.nonExclusivePackages.length > 0 ||
        visibleOptionalPackageGroups.exclusivePackages.length > 0 ||
        (!hasMandatoryVAD && vadKeys.length > 0));

    const packageContent = (
      <Accordion activeKey={activeKeys} alwaysOpen>
        {showIncludedPackages && (
          <Accordion.Item eventKey="0" className="accordionItem">
            <Accordion.Header as="div">
              <CustomToggle
                eventKey="0"
                activeKeys={activeKeys}
                setActiveKeys={setActiveKeys}
              >
                Included Packages
              </CustomToggle>
            </Accordion.Header>
            <Accordion.Body>
              {hasMandatoryVAD && renderVADSection()}
              {renderPackageGroup(includedEntries, false)}
            </Accordion.Body>
          </Accordion.Item>
        )}

        {showOptionalPackages && (
          <Accordion.Item eventKey="1" className="accordionItem">
            <Accordion.Header as="div">
              <CustomToggle
                eventKey="1"
                activeKeys={activeKeys}
                setActiveKeys={setActiveKeys}
              >
                Optional Packages
              </CustomToggle>
            </Accordion.Header>
            <Accordion.Body>
              {!hasMandatoryVAD && renderVADSection()}
              {renderPackageGroup(optionalEntries, true)}
            </Accordion.Body>
          </Accordion.Item>
        )}
      </Accordion>
    );

    const updateButton = !readOnly && (
      <div className="d-flex justify-content-end">
        <button
          type="button"
          className={classNames("btn rounded-pill w-100", formSubmitButton)}
          onClick={() => {
            setShow(false);
            onUpdate?.(packagesObj, optedCoversObj);
          }}
        >
          Update
        </button>
      </div>
    );

    const handleShow = () => {
      resetPackagesState();
      setActiveKeys(readOnly ? ["0"] : ["1"]);
      setCoverActiveKeys(["0", "1"]);
      setShow(true);
    };

    const renderCoversContent = () => {
      const coversEntries = Object.entries(optedCoversObj || {})
        .filter(([, c]) => !!c)
        .filter(([, c]) => coverHasAnyNonZeroValue(c))
        .filter(([k, c]) => handleAdvanceCover(k,c));

      const includedCoverEntries = coversEntries.filter(
        ([, cover]) => cover?.isIncludedCover
      );
      const optionalCoverEntries = coversEntries.filter(
        ([, cover]) => !cover?.isIncludedCover
      );
      const showOptionalCovers = !readOnly && optionalCoverEntries.length > 0;

      if (!includedCoverEntries.length && !showOptionalCovers) {
        return (
          <div className="package-group mb-3">
            <span className="package-label m-0">
              No add-on covers are available for this plan.
            </span>
          </div>
        );
      }

      const renderCoverGroup = (coverEntries, isIncluded) => (
        <>
          {coverEntries.map(([code, cover]) => {
            const amount = getCoverDisplayAmount(cover);
            const amountStr = formatCoverAmount(amount);
            const isHealthSupercharge = String(subProductCode || "") === HEALTH_SUPERCHARGE_CODE;
            const showSubSelection =
              Array.isArray(cover.value) &&
              cover.value.length > 1 &&
              ( isHealthSupercharge ? code !== "C004" : true);

            const canToggle = canUserToggleCover(cover);
            const checkboxDisabled =
              (isAggregateDeductibleCover(code) &&
                cover?.opted &&
                isIncluded) ||
              (isIncluded && !userCanOptOutMandatory) || !canToggle || (isHealthSupercharge && code === "C004");
            const subSelectionDisabled =
              readOnly ||
              !cover?.opted ||
              (isIncluded &&
                !userCanOptOutMandatory &&
                !isAggregateDeductibleCover(code));
            const visibleCoverOptions = Array.isArray(cover.value)
              ? isIncluded &&
                !isAggregateDeductibleCover(code) &&
                cover.value.some((opt) => opt?.opted === true)
                ? cover.value.filter((opt) => opt?.opted === true)
                : cover.value
              : [];

            return (
              <div key={code} className="package-group mb-3">
                <div className="label-checkbox-container d-flex justify-content-between align-items-center">
                  <label className="package-label m-0">
                    <span>
                      <Image
                        src={`${IMAGE_BASE_PATH}/images/StarIcon.svg`}
                        alt="X"
                        width={15}
                        height={15}
                      />
                    </span>
                    {cover?.name}
                  </label>

                  <input
                    type="checkbox"
                    checked={!!cover?.opted}
                    disabled={checkboxDisabled}
                    className={styles.coverCheckbox}
                    onChange={() => handleOptedCoversChange(code)}
                  />
                </div>

                <div className="price-dropdown-container d-flex align-items-center justify-content-between mt-1">
                  <span className="price-info">{amountStr}</span>
                </div>

                {showSubSelection && (
                  <div
                    className={classNames(
                      styles.radioGroup,
                      styles.coverOptionsGrid
                    )}
                  >
                    {visibleCoverOptions.map((opt) => {
                      const optAmount = Number(opt?.value || 0) || 0;
                      const optAmountStr = formatCoverAmount(optAmount);
                      const optionDisabled =
                        subSelectionDisabled ||
                        !canSelectCoverOption(code, cover, opt);

                      return (
                        <div
                          key={opt?.name}
                          className={classNames(
                            styles.radioInputWrapper,
                            styles.coverOption
                          )}
                        >
                          <input
                            className={styles.radioInput}
                            type="radio"
                            name={`cover-${code}-option`}
                            id={`cover-${code}-${opt?.name}`}
                            checked={opt?.opted === true}
                            disabled={optionDisabled}
                            onChange={() =>
                              handleCoverOptionChangeRadio(code, opt?.name)
                            }
                          />
                          <label
                            className="form-check-label"
                            htmlFor={`cover-${code}-${opt?.name}`}
                          >
                            {formatCoverOptionName(opt?.name)}{" "}
                            <span style={{ opacity: 0.8 }}>
                              ({optAmountStr})
                            </span>
                          </label>

                          {/*
                            CHECKBOX VERSION (if later multiple selections allowed)
                            <input
                              type="checkbox"
                              checked={opt?.opted === true}
                              disabled={readOnly}
                              onChange={() => handleCoverOptionChangeCheckbox(code, opt?.name)}
                            />
                          */}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </>
      );

      return (
        <Accordion activeKey={coverActiveKeys} alwaysOpen>
          {includedCoverEntries.length > 0 && (
            <Accordion.Item eventKey="0" className="accordionItem">
              <Accordion.Header as="div">
                <CustomToggle
                  eventKey="0"
                  activeKeys={coverActiveKeys}
                  setActiveKeys={setCoverActiveKeys}
                >
                  Included Covers
                </CustomToggle>
              </Accordion.Header>
              <Accordion.Body>
                {renderCoverGroup(includedCoverEntries, true)}
              </Accordion.Body>
            </Accordion.Item>
          )}

          {showOptionalCovers && (
            <Accordion.Item eventKey="1" className="accordionItem">
              <Accordion.Header as="div">
                <CustomToggle
                  eventKey="1"
                  activeKeys={coverActiveKeys}
                  setActiveKeys={setCoverActiveKeys}
                >
                  Optional Covers
                </CustomToggle>
              </Accordion.Header>
              <Accordion.Body>
                {renderCoverGroup(optionalCoverEntries, false)}
              </Accordion.Body>
            </Accordion.Item>
          )}
        </Accordion>
      );
    };

    return (
      <>
        <span>
          <button type="button" className="link-btn" onClick={handleShow}>
            {readOnly ? "View" : packageCount === 0 ? "Add" : "Update"}
          </button>
        </span>

        {isMobile() ? (
          <Modal
            show={show}
            onHide={() => setShow(false)}
            className={styles.modal}
            overlayClassName={styles.overlay}
          >
            <Modal.Header closeButton>
              <Modal.Title>
                {isCoversUIEnabled && !isPackageUIEnabled
                  ? "Covers"
                  : isCoversUIEnabled
                  ? "Packages & Covers"
                  : "Packages"}
                <div className="package-sub-header">
                  {isCoversUIEnabled
                    ? activeView === "packages"
                      ? "Select the packages you want in your customised plan."
                      : "Select the add-on covers you want in your customised plan."
                    : "Select the packages you want in your customised plan."}
                </div>

                {isCoversUIEnabled && isPackageUIEnabled && (
                  <div className={styles.toggleHeaderWrapper}>
                    <div className={styles.toggleWrapper}>
                      <button
                        className={classNames(
                          styles.toggleBtn,
                          activeView === "packages" && styles.active
                        )}
                        onClick={() => setActiveView("packages")}
                      >
                        Packages
                      </button>

                      <button
                        className={classNames(
                          styles.toggleBtn,
                          activeView === "covers" && styles.active
                        )}
                        onClick={() => setActiveView("covers")}
                      >
                        Covers
                      </button>
                    </div>
                  </div>
                )}
              </Modal.Title>
            </Modal.Header>

            <Modal.Body className={styles.modalBody}>
              {isPackageUIEnabled && (
                <div
                  style={{
                    display: activeView === "packages" ? "block" : "none",
                  }}
                >
                  {packageContent}
                </div>
              )}
              {isCoversUIEnabled && (
                <div
                  style={{
                    display:
                      !isPackageUIEnabled || activeView === "covers"
                        ? "block"
                        : "none",
                  }}
                >
                  {renderCoversContent()}
                </div>
              )}
            </Modal.Body>

            <div className={styles.fixedFooter}>{updateButton}</div>
          </Modal>
        ) : (
          <Offcanvas
            className={classNames(formOffCanvas, "rQuoteCard")}
            show={show}
            onHide={() => setShow(false)}
            placement="end"
          >
            <Offcanvas.Header>
              <Offcanvas.Title className={styles.offcanvasTitleOverride}>
                {isCoversUIEnabled && !isPackageUIEnabled
                  ? "Covers"
                  : isCoversUIEnabled
                  ? "Packages & Covers"
                  : "Packages"}
                <div className="package-sub-header">
                  {isCoversUIEnabled
                    ? activeView === "packages"
                      ? "Select the packages you want in your customised plan."
                      : "Select the add-on covers you want in your customised plan."
                    : "Select the packages you want in your customised plan."}
                </div>

                {isCoversUIEnabled && isPackageUIEnabled && (
                  <div className={styles.toggleHeaderWrapper}>
                    <div className={styles.toggleWrapper}>
                      <button
                        className={classNames(
                          styles.toggleBtn,
                          activeView === "packages" && styles.active
                        )}
                        onClick={() => setActiveView("packages")}
                      >
                        Packages
                      </button>

                      <button
                        className={classNames(
                          styles.toggleBtn,
                          activeView === "covers" && styles.active
                        )}
                        onClick={() => setActiveView("covers")}
                      >
                        Covers
                      </button>
                    </div>
                  </div>
                )}
              </Offcanvas.Title>

              <div
                aria-hidden="true"
                className="mb-auto pointer"
                onClick={() => setShow(false)}
              >
                <Image
                  src={`${IMAGE_BASE_PATH}/images/close.svg`}
                  alt="X"
                  width={15}
                  height={15}
                />
              </div>
            </Offcanvas.Header>

            <Offcanvas.Body>
              {isPackageUIEnabled && (
                <div
                  style={{
                    display: activeView === "packages" ? "block" : "none",
                  }}
                >
                  {packageContent}
                </div>
              )}
              {isCoversUIEnabled && (
                <div
                  style={{
                    display:
                      !isPackageUIEnabled || activeView === "covers"
                        ? "block"
                        : "none",
                  }}
                >
                  {renderCoversContent()}
                </div>
              )}
            </Offcanvas.Body>

            {updateButton}
          </Offcanvas>
        )}
      </>
    );
  }
);

PackageCanvasMenu.propTypes = {
  packages: PropTypes.object.isRequired,
  userCanOptOutMandatory: PropTypes.bool.isRequired,
  readOnly: PropTypes.bool.isRequired,
  onUpdate: PropTypes.func,
  addonCovers: PropTypes.object,
  optedCovers: PropTypes.object,
  packageCount: PropTypes.number,
  subProductCode: PropTypes.string,
  toProductCode: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

PackageCanvasMenu.displayName = "PackageCanvasMenu";

export default PackageCanvasMenu;
